import { useCallback, useState } from 'react'

import { Tooltip } from '@/components/ui/tooltip'
import { DIALOG_TYPE } from '@/dialogs/constants.ts'
import { openDialog } from '@/models/dialogs.ts'
import type { MassMessage } from '@/modules/chatting/types'
import type { BulkMessage } from '@/modules/online-mass-messages/types.ts'

import CancelledIcon from '../../../../icons/cancelled.svg'
import InfoIcon from '../../../../icons/info.svg'

interface Props {
  canUnsend?: boolean
  id: MassMessage['id']
  sentCount: number
  mutationFn: (id: BulkMessage['id']) => Promise<unknown>
}

export const UnsendButton = ({
  canUnsend,
  id,
  sentCount,
  mutationFn,
}: Props) => {
  const [isLoading, setIsLoading] = useState(false)

  const handleClick = useCallback(() => {
    openDialog({
      type: DIALOG_TYPE.CONFIRMATION,
      title: `Please confirm you want to unsend this message from ${sentCount} chats`,
      onConfirm: async () => {
        try {
          setIsLoading(true)
          await mutationFn(id)
        } finally {
          setIsLoading(false)
        }
      },
    })
  }, [id, mutationFn, sentCount])

  if (!canUnsend) {
    return (
      <Tooltip content="Unsent">
        <CancelledIcon />
      </Tooltip>
    )
  }

  return (
    <div className="flex gap-1.5">
      <button
        className="text-primary-500 disabled:opacity-50"
        onClick={handleClick}
        disabled={isLoading}
      >
        Unsend
      </button>
      <Tooltip
        contentClassName="max-w-[280px]"
        content="This will delete the message for all recipients who have not opened the message"
      >
        <InfoIcon className="text-secondary-300" />
      </Tooltip>
    </div>
  )
}
